import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
} from '@mui/material';
import {
  Layers,
  X,
  Clock,
  CheckCircle2,
  AlertTriangle,
  BookOpen,
  ArrowRight,
  Briefcase,
  Search,
} from 'lucide-react';
import api from '../../../../api/client';
import { calculateInterviewDurationRequirements } from '../../../../utils/durationCalculator';
import {
  getExperienceLevelLabel,
  getExperienceLevelColor,
  getCategoryBreakdown,
  profileQuestionToJdQuestion,
} from './interviewProfileUtils';

export default function InterviewProfilePickerModal({
  open,
  onClose,
  onAttach,
  onDetach = null,
  attachedProfileId = null,
  existingQuestions = [],
  jobDescriptionId = null,
}) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [selectedProfileDetail, setSelectedProfileDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [attachMode, setAttachMode] = useState('append'); // 'append' | 'replace'
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [confirmReplaceWarning, setConfirmReplaceWarning] = useState(null);

  // Fetch profiles list when modal opens
  useEffect(() => {
    if (open) {
      setLoading(true);
      setErrorMsg('');
      setConfirmReplaceWarning(null);
      setSearchQuery('');
      api
        .get('interview-profiles/')
        .then((res) => {
          const list = res.data?.results ? res.data.results : Array.isArray(res.data) ? res.data : [];
          setProfiles(list);
          if (list.length > 0) {
            // Prioritize selecting the currently attached profile if available
            if (attachedProfileId && list.some((p) => p.id === attachedProfileId)) {
              setSelectedProfileId(attachedProfileId);
            } else if (!selectedProfileId || !list.some((p) => p.id === selectedProfileId)) {
              setSelectedProfileId(list[0].id);
            }
          }
        })
        .catch((err) => {
          console.error('Failed to load interview profiles:', err);
          setErrorMsg('Failed to load interview profiles.');
        })
        .finally(() => setLoading(false));
    }
  }, [open, attachedProfileId]);

  // Fetch full detail of selected profile (with questions)
  useEffect(() => {
    if (selectedProfileId) {
      setLoadingDetail(true);
      api
        .get(`interview-profiles/${selectedProfileId}/`)
        .then((res) => {
          setSelectedProfileDetail(res.data);
        })
        .catch((err) => {
          console.error('Failed to load profile details:', err);
        })
        .finally(() => setLoadingDetail(false));
    } else {
      setSelectedProfileDetail(null);
    }
  }, [selectedProfileId]);

  // Filter profiles based on search query
  const filteredProfiles = useMemo(() => {
    if (!searchQuery.trim()) return profiles;
    const q = searchQuery.toLowerCase().trim();
    return profiles.filter((p) => {
      const nameMatch = (p.name || '').toLowerCase().includes(q);
      const roleMatch = (p.target_role || '').toLowerCase().includes(q);
      const descMatch = (p.description || '').toLowerCase().includes(q);
      return nameMatch || roleMatch || descMatch;
    });
  }, [profiles, searchQuery]);

  // Filter out any questions from the currently attached profile
  const nonProfileExistingQuestions = useMemo(() => {
    if (!attachedProfileId) return existingQuestions;
    return existingQuestions.filter((q) => {
      const qProfId = q.profileId || q.profile_id;
      if (qProfId && String(qProfId) === String(attachedProfileId)) {
        return false;
      }
      // If questions are tagged with any profileId when replacing an attached profile
      if (attachedProfileId && qProfId && String(qProfId) !== String(selectedProfileId)) {
        return false;
      }
      return true;
    });
  }, [existingQuestions, attachedProfileId, selectedProfileId]);

  // Candidate duration requirement preview
  const durationPreview = useMemo(() => {
    if (!selectedProfileDetail?.profile_questions) return null;
    const profileJdQuestions = selectedProfileDetail.profile_questions.map((q) =>
      profileQuestionToJdQuestion(q, null, selectedProfileDetail?.name)
    );

    let resultingQuestions = [];
    if (attachMode === 'replace') {
      resultingQuestions = profileJdQuestions;
    } else {
      resultingQuestions = [...nonProfileExistingQuestions, ...profileJdQuestions];
    }

    return {
      resultingCount: resultingQuestions.length,
      duration: calculateInterviewDurationRequirements(resultingQuestions),
    };
  }, [selectedProfileDetail, attachMode, nonProfileExistingQuestions]);

  const handleAttach = async (force = false) => {
    if (!selectedProfileDetail) return;
    const profileQuestions = selectedProfileDetail.profile_questions || [];
    if (profileQuestions.length === 0) {
      setErrorMsg('Cannot attach an empty profile.');
      return;
    }

    setSubmitting(true);
    setErrorMsg('');

    // If existing JD ID is present, attach on server
    if (jobDescriptionId) {
      try {
        const res = await api.post(`job-descriptions/${jobDescriptionId}/attach-profile/`, {
          profile_id: selectedProfileId,
          mode: attachMode,
          force: force,
          current_questions: existingQuestions,
        });

        if (onAttach) {
          onAttach(res.data.questions, attachMode, res.data.duration_info, selectedProfileDetail);
        }
        onClose();
      } catch (err) {
        console.error('Server attach failed:', err);
        const data = err.response?.data;
        if (data?.warning === 'ACTIVE_INVITES_EXIST') {
          setConfirmReplaceWarning(data.detail);
        } else {
          setErrorMsg(data?.detail || data?.error || 'Failed to attach profile to job description.');
        }
      } finally {
        setSubmitting(false);
      }
    } else {
      // Client-side attach for newly created JD
      const transformed = profileQuestions.map((q) =>
        profileQuestionToJdQuestion(q, null, selectedProfileDetail?.name)
      );
      let resultingQuestions = [];
      if (attachMode === 'replace') {
        resultingQuestions = transformed;
      } else {
        resultingQuestions = [...nonProfileExistingQuestions, ...transformed];
      }
      if (onAttach) {
        onAttach(resultingQuestions, attachMode, durationPreview?.duration, selectedProfileDetail);
      }
      onClose();
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', minHeight: '60vh' } }}
    >
      <DialogTitle className="flex items-center justify-between border-b pb-3 font-bold text-gray-800">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
            <Layers className="w-5 h-5" />
          </div>
          <span>Attach Interview Profile</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </DialogTitle>

      <DialogContent className="pt-4 space-y-4">
        {errorMsg && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {confirmReplaceWarning && (
          <div className="p-4 bg-amber-50 border border-amber-300 rounded-xl space-y-2 text-xs text-amber-900">
            <div className="flex items-center gap-2 font-bold text-sm text-amber-900">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
              <span>Outstanding Invites Detected</span>
            </div>
            <p>{confirmReplaceWarning}</p>
            <div className="pt-2 flex items-center gap-3">
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={() => handleAttach(true)}
                disabled={submitting}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Yes, Replace Questions Anyway
              </Button>
              <Button
                size="small"
                onClick={() => setConfirmReplaceWarning(null)}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <CircularProgress size={32} />
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 border border-dashed rounded-xl">
            <Layers className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <h4 className="font-semibold text-gray-700">No Interview Profiles available</h4>
            <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
              Please create an Interview Profile in the Interview Library first to attach it to job descriptions.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* Left Column: Profile Selector */}
            <div className="md:col-span-5 flex flex-col max-h-[420px]">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">
                  Select Profile
                </label>
                <span className="text-[11px] text-gray-400 font-medium">
                  {filteredProfiles.length} of {profiles.length}
                </span>
              </div>

              {/* Search Bar */}
              <div className="relative mb-2.5">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search profiles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-7 py-1.5 text-xs bg-slate-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1.5 focus:ring-blue-500/40 focus:border-blue-500 transition-all placeholder:text-gray-400"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 rounded-full"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>

              <div className="space-y-2 overflow-y-auto pr-1 flex-1">
                {filteredProfiles.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-xs bg-slate-50 rounded-lg border border-dashed">
                    No profiles match "{searchQuery}"
                  </div>
                ) : (
                  filteredProfiles.map((p) => {
                    const isSelected = p.id === selectedProfileId;
                    const isAttached = p.id === attachedProfileId;
                    return (
                      <div
                        key={p.id}
                        onClick={() => setSelectedProfileId(p.id)}
                        className={`p-3 rounded-xl border cursor-pointer transition-all ${
                          isAttached
                            ? isSelected
                              ? 'border-emerald-500 bg-emerald-50/80 ring-2 ring-emerald-500/30 shadow-sm'
                              : 'border-emerald-300 bg-emerald-50/40 hover:bg-emerald-50/70'
                            : isSelected
                            ? 'border-indigo-600 bg-indigo-50/50 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-sm text-gray-900 truncate">{p.name}</span>
                          {isAttached ? (
                            <div className="flex items-center gap-1.5 shrink-0">
                              <span className="text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 px-1.5 py-0.5 rounded flex items-center gap-1 shadow-2xs">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                                Attached
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onDetach) onDetach(p);
                                }}
                                className="w-4 h-4 rounded-full bg-emerald-200 hover:bg-red-100 text-emerald-800 hover:text-red-600 flex items-center justify-center transition-colors cursor-pointer"
                                title={`Remove "${p.name}" from this Job Description`}
                              >
                                <X className="w-2.5 h-2.5 stroke-[2.8]" />
                              </button>
                            </div>
                          ) : (
                            <span className="text-[10px] font-semibold bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded shrink-0">
                              {p.question_count || 0} Qs
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                          <span className="truncate">{p.target_role || 'General'}</span>
                          <span className="capitalize shrink-0">{p.experience_level}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Right Column: Profile Preview & Attach Options */}
            <div className="md:col-span-7 bg-slate-50/80 rounded-xl p-4 border border-gray-200 flex flex-col justify-between">
              {loadingDetail ? (
                <div className="flex items-center justify-center py-16">
                  <CircularProgress size={24} />
                </div>
              ) : selectedProfileDetail ? (
                <div className="space-y-4">
                  {/* Currently attached notice if this profile is already attached */}
                  {selectedProfileDetail.id === attachedProfileId && (
                    <div className="p-2.5 bg-emerald-50 border border-emerald-300 rounded-xl flex items-center justify-between gap-2 text-xs text-emerald-900 shadow-2xs">
                      <div className="flex items-center gap-2 font-semibold">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
                        <span>Currently attached to this Job Description</span>
                      </div>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onDetach && onDetach(selectedProfileDetail)}
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          py: 0.25,
                          px: 1.25,
                          borderColor: '#fca5a5',
                          color: '#dc2626',
                          background: '#fff',
                          '&:hover': {
                            borderColor: '#ef4444',
                            background: '#fee2e2',
                          },
                        }}
                      >
                        Remove Profile
                      </Button>
                    </div>
                  )}

                  <div>
                    <h3 className="font-bold text-base text-gray-900">{selectedProfileDetail.name}</h3>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {selectedProfileDetail.description || 'Standard interview question template.'}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded bg-indigo-100 text-indigo-800">
                        {selectedProfileDetail.target_role || 'General Role'}
                      </span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded bg-purple-100 text-purple-800">
                        {getExperienceLevelLabel(selectedProfileDetail.experience_level)}
                      </span>
                    </div>
                  </div>

                  {/* Mode Selector */}
                  <div className="bg-white p-3 rounded-xl border border-gray-200 space-y-1">
                    <span className="text-xs font-bold text-gray-700 block">Attachment Mode</span>
                    <RadioGroup
                      value={attachMode}
                      onChange={(e) => setAttachMode(e.target.value)}
                    >
                      <FormControlLabel
                        value="append"
                        control={<Radio size="small" />}
                        label={
                          <div className="text-xs">
                            <span className="font-semibold text-gray-900">
                              {attachedProfileId
                                ? 'Replace profile questions (keep custom questions)'
                                : 'Append questions to existing'}
                            </span>
                            <span className="text-gray-500 block">
                              {attachedProfileId
                                ? `Removes previous profile questions and adds questions from "${selectedProfileDetail.name}". Custom/manual questions (${nonProfileExistingQuestions.length}) are kept.`
                                : `Keeps current questions (${existingQuestions.length}) and adds profile questions (Unlimited total allowed).`}
                            </span>
                          </div>
                        }
                      />
                      <FormControlLabel
                        value="replace"
                        control={<Radio size="small" />}
                        label={
                          <div className="text-xs">
                            <span className="font-semibold text-gray-900">Replace all questions</span>
                            <span className="text-gray-500 block">
                              Removes all current questions in this JD and sets questions strictly from this profile.
                            </span>
                          </div>
                        }
                      />
                    </RadioGroup>
                  </div>

                  {/* Duration Estimation Banner */}
                  {durationPreview && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-900 space-y-1">
                      <div className="flex items-center justify-between font-bold">
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4 text-blue-600" />
                          Calculated Duration After Attach
                        </span>
                        <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-[11px]">
                          {durationPreview.duration.recommended_duration_minutes} min
                        </span>
                      </div>
                      <p className="text-[11px] text-blue-700">
                        Total {durationPreview.resultingCount} questions will be in this JD. Minimum required interview time: {durationPreview.duration.minimum_duration_minutes} min.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-400 text-xs">Select a profile on the left to preview</div>
              )}
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions className="border-t px-6 py-3">
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={() => handleAttach(false)}
          variant="contained"
          disabled={submitting || !selectedProfileDetail || selectedProfileDetail.profile_questions?.length === 0}
          sx={{
            borderRadius: '10px',
            px: 3,
            textTransform: 'none',
            fontWeight: 700,
            background: 'linear-gradient(135deg, #2563eb 0%, #9333ea 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1d4ed8 0%, #7e22ce 100%)',
            },
          }}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : selectedProfileDetail?.id === attachedProfileId ? (
            `Re-sync ${selectedProfileDetail?.profile_questions?.length || 0} Questions`
          ) : attachedProfileId ? (
            `Replace & Attach Profile`
          ) : (
            `Attach Profile`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

